import axios from 'axios';
import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  DropdownOption,
  SearchesAssign,
  Property,
  OrderEntryName,
  File,
  SubmitionError,
  Search,
  PropertyError,
  IndexedPropertyErrors,
} from './types';
import { useHistory, useParams } from 'react-router-dom';
import convertToFileDownload from './FileDownloads';
import produce from 'immer';
import qs from 'qs';
import { PropertyMatch, Result } from '../AddressLookup/types';
import { trimEnd } from '../../components/utilities/functions';

interface Props {
  children: React.ReactNode;
}
const defaultPriority = { value: 0, label: 'Priority: Regular' };

interface ContextType {
  selectedClient: DropdownOption;
  setSelectedClient: (e: DropdownOption) => void;
  titleNumber: string;
  priority: DropdownOption;
  setTitleNumber: React.Dispatch<React.SetStateAction<string>>;
  setPriority: React.Dispatch<DropdownOption>;
  searchesAssignObj: SearchesAssign;
  setSearchesAssignObj: React.Dispatch<React.SetStateAction<SearchesAssign>>;
  properties: Property[];
  setProperties: React.Dispatch<React.SetStateAction<Property[]>>;
  orderEntryNames: OrderEntryName[];
  setOrderEntryNames: React.Dispatch<React.SetStateAction<OrderEntryName[]>>;
  submitOrder: () => void;
  files: File[];
  setFiles: React.Dispatch<React.SetStateAction<File[]>>;
  setIsPurchase: React.Dispatch<React.SetStateAction<boolean>>;
  isPurchase: boolean;
  submitting: boolean;
  errors: SubmitionError;
  propertyErrors: IndexedPropertyErrors;
  validateProperty: () => boolean;
  setAddedSearches: React.Dispatch<React.SetStateAction<Search[]>>;
  setTemplateSearchTypes: React.Dispatch<React.SetStateAction<Search[]>>;
  templateSearchTypes: Search[];
  addedSearches: Search[];
  clientNote: string;
  setClientNote: React.Dispatch<React.SetStateAction<string>>;
  openErrorModal: boolean;
  setOpenErrorModal: React.Dispatch<React.SetStateAction<boolean>>;
  openSellerErrorModal: boolean;
  setOpenSellerErrorModal: React.Dispatch<React.SetStateAction<boolean>>;
  changePropertyInfo: (label: keyof Partial<Property>, value: any, index: number) => void;
  addSellersToNames: (names: any[]) => void;
  deleteListOfNames: (name: string[]) => void;
  fromAddressLookup: boolean;
}

const defaultInitialNameType = { value: 1, label: 'Seller' };
const defaultBorrowerType = { value: 1, label: 'Borrower' };
const defaultSellertype = { value: 2, label: 'Seller' };
const createProperty = () => {
  const property: Property = {
    address: '',
    addressLine2: '',
    undeterminedLocality: false,
    zip: '',
    district: '',
    section: '',
    block: '',
    lot: '',
    qualifier: '',
    isDuplicate: false,
    includeMunicipalSearches: false,
    isValidated: false,
    rpadId: null,
    municipalityId: null,
    subMunicipalityId: null,
    duplicates: null,
    countyOnly: false,
    rpadPropertyId: '',
    printKey: '',
    owners: null,
    fullValidationNote: null,
    validationNote: '',
    preConvertedFiles: null,
    validationFiles: null,
    isResidential: null,
    unvalidatedOwner: null,
    selectedCounty: null,
    overrideMode: false,
    isNyc: false,
    hamletId: null,
    selectedCity: {
      county: '',
      municipality: '',
      municipalityId: null,
      municipalityType: '',
      stateCode: '',
      stateName: '',
      subMunicipality: '',
      subMunicipalityId: null,
      subMunicipalityType: '',
      swisCode: '',
      rpadSwisCode: '',
      countyId: null,
      id: null,
      municipalityTypeId: null,
      subMunicipalityTypeId: null,
      hamletId: null,
      hamletName: '',
    },
  };

  return property;
};
const OrderEntryContext = createContext<ContextType | undefined>(undefined);

const labelsMarkUnvalidated: (keyof Property)[] = [
  'address',
  'block',
  'section',
  'qualifier',
  'lot',
  'addressLine2',
];

export const OrderEntryProvider = (props: Props) => {
  const [selectedClient, setSelectedClient] = useState<DropdownOption>();
  const [titleNumber, setTitleNumber] = useState('');
  const [priority, setPriority] = useState<DropdownOption>(defaultPriority);
  const [searchesAssignObj, setSearchesAssignObj] = useState<SearchesAssign>({});
  const [properties, setProperties] = useState<Property[]>([]);
  const [orderEntryNames, setOrderEntryNames] = useState<OrderEntryName[]>([]);
  const [files, setFiles] = useState<File[]>([]);
  const [isPurchase, setIsPurchase] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<SubmitionError>(null);
  const [propertyErrors, setPropertyErrors] = useState<IndexedPropertyErrors>({});
  const [openErrorModal, setOpenErrorModal] = useState(false);
  const [openSellerErrorModal, setOpenSellerErrorModal] = useState(false);
  const [templateSearchTypes, setTemplateSearchTypes] = useState<Search[]>([]);
  const [addedSearches, setAddedSearches] = useState<Search[]>([]);
  const [clientNote, setClientNote] = useState('');
  const [fromAddressLookup, setFromAddressLookup] = useState(false);
  const { propertyId, location } = useParams<{ propertyId: string; location: string }>();

  const oeNamesCheck = orderEntryNames.find(oen => oen.type.label === 'Seller' && oen.name) !== undefined;
  const { push } = useHistory();
  const getPropertyInfo = async () => {
    const queryOptions = {
      rpadId: propertyId,
    };
    const stringifiedParams = qs.stringify(queryOptions);
    const baseUrl = `/api/clientproperty/validateRpad/${location}`;
    const { data } = await axios.get<PropertyMatch>(`${baseUrl}?${stringifiedParams}`);
    if (data.totalCount !== 1) {
      setProperties([createProperty()]);
    } else {
      const m = data.results[0];
      const property = {
        address: m.address,
        block: location === 'NJ' ? trimEnd(m.property.block, '.') : m.property.block,
        countyOnly: false,
        district: m.muniTypes[2]?.district ?? m.muniTypes[1]?.district,
        includeMunicipalSearches: true,
        isDuplicate: false,
        isValidated: true,
        lot: location === 'NJ' ? trimEnd(m.property.lot, '.') : m.property.lot,
        municipalityId: m.muniTypes[1].id,
        subMunicipalityId: m.muniTypes[2]?.id,
        owners: m.owners,
        printKey: m.printKey,
        rpadPropertyId: m.id,
        section: m.property.section,
        qualifier: m.property.qualifier,
        zip: m.location.zip,
        addressLine2: m.aptNo,
        validationNote: '',
        validationFiles: [],
        selectedCounty: null,
        buildingCode: m.property.propertyClass,
        selectedCity: {
          county: m.muniTypes[0]?.name,
          countyId: m.muniTypes[0]?.id,
          municipality: m.muniTypes[1]?.name,
          municipalityId: m.muniTypes[1]?.id,
          municipalityType: m.muniTypes[1]?.type,
          subMunicipality: m.muniTypes[2]?.name,
          subMunicipalityType: m.muniTypes[2]?.type,
          subMunicipalityId: m.muniTypes[2]?.id,
          swisCode: m.muniTypes[2]?.swisCode || m.muniTypes[1]?.swisCode,
          stateCode: m.muniTypes[0]?.stateCode,
          stateName: m.muniTypes[0]?.state,
        },
      };
      setProperties([property]);
    }
  };
  useEffect(() => {
    if (propertyId) {
      getPropertyInfo();
      setFromAddressLookup(true);
    } else {
      setFromAddressLookup(false);
      setOrderEntryNames([]);
      setProperties([createProperty()]);
    }
  }, [propertyId]);
  const validateForm = () => {
    const errors: SubmitionError = {};
    if (!titleNumber) {
      errors.titleNumber = 'Title Number Required';
    }
    if (properties.length < 1) {
      errors.properties = 'Needs at least 1 Property';
      setOpenErrorModal(true);
    }
    if (validateProperty()) {
      errors.properties = 'Property missing info';
    }
    // if (!oeNamesCheck && clientNote === '') {
    //   errors.names = 'Needs at least 1 seller or note';
    //   setOpenSellerErrorModal(true);
    // }
    if (templateSearchTypes.concat(addedSearches).length < 1) {
      errors.searches = 'Needs at least 1 Search';
    }
    setErrors(errors);
    return Object.keys(errors).length === 0 ? null : errors;
  };
  const validateProperty = () => {
    const indexedPropertyErrors: IndexedPropertyErrors = properties.reduce((pre, p, index) => {
      let error: PropertyError = {};
      let hasError = false;
      if (!p.address) {
        error.address = 'Missing Address';
        hasError = true;
      }
      if (
        (!p.undeterminedLocality && !p.selectedCity?.municipalityId) ||
        (p.undeterminedLocality && !p.undeterminedLocality)
      ) {
        error.municipality = 'Missing Municipality';
        hasError = true;
      }
      if (hasError) {
        pre[index] = error;
      }
      return pre;
    }, {});
    setPropertyErrors(indexedPropertyErrors);

    const keys = Object.keys(indexedPropertyErrors);

    return keys.length > 0;
  };

  async function checkDuplicate(matchedProperty: Property) {
    const params = qs.stringify({
      address: matchedProperty.address,
      stateCode: matchedProperty.selectedCity?.stateCode,
      section: matchedProperty.section,
      block: matchedProperty.block,
      lot: matchedProperty.lot,
      qualifier: matchedProperty.qualifier,
      swisCode: matchedProperty.selectedCity?.swisCode,
      rpadPropertyId: matchedProperty.rpadPropertyId,
    });

    const { data } = await axios.get(`/api/clientproperty/duplicatecheck?${params}`);
    return data;
  }

  const submitOrder = async () => {
    setSubmitting(true);
    const err = validateForm();
    if (err) {
      setSubmitting(false);
      return;
    }

    const propertiesWithDuplicates = await Promise.all(
      properties.map(async p => {
        if (!p.selectedCity?.municipalityId) {
          return { ...p };
        }
        const data = await checkDuplicate(p);
        return data.length > 0 ? { ...p, duplicates: data.length, oldOrder: data[0] } : { ...p };
      })
    );
    const searchTypes = templateSearchTypes.concat(addedSearches);
    const order = {
      titleNumber,
      priority: priority.value,
      selectedClient: selectedClient.value,
      files,
      properties: propertiesWithDuplicates.map(p => {
        if (!p.selectedCity?.municipalityId) {
          return {
            address: p.address,
            addressLine2: p.addressLine2,
            includeMunicipalSearches: true,
            undeterminedLocality: p.undeterminedLocality,
            zip: p.zip,
            section: p.section,
            block: p.block,
            lot: p.lot,
            qualifier: p.qualifier,
          };
        } else {
          return { ...p };
        }
      }),
      searchTypes: searchTypes,
      clientNote,
      orderEntryNames: orderEntryNames
        .filter(oe => oe.name?.trim())
        .map(oen => ({
          ...oen,
          checkBankruptcy: searchTypes.find(s => s.name === 'Bankruptcy Search') ? true : false,
          checkPatriot: searchTypes.find(s => s.name === 'Patriot Search') ? true : false,
          checkCogs: searchTypes.find(s => s.name === 'Certificate of Good Standing') ? true : false,
          checkFranchise: searchTypes.find(s => s.name === 'Franchise Tax') ? true : false,
          checkArticles: searchTypes.find(s => s.name === 'Articles of organization') ? true : false,
          type:
            oen.type.label === 'Buyer'
              ? defaultBorrowerType
              : oen.type.label === 'Seller'
              ? defaultSellertype
              : oen.type,
        })),
      isPurchase,
    };

    const { status, data } = await axios.post('/api/orderEntry/add', order);
    setSubmitting(false);
    if (status === 200) {
      localStorage.removeItem('selectedProperties');
      push(`/order-confirmation/success/${data.orderId}`);
    }
  };
  const changePropertyInfo = (label: keyof Partial<Property>, value: any, index: number) => {
    const newProperties = produce(properties, draft => {
      (draft[index][label] as keyof Property) = value;
      if (label === 'selectedCity') {
        draft[index].municipalityId = value?.municipalityId;
        draft[index].subMunicipalityId = value?.subMunicipalityId;
        draft[index].hamletId = value?.hamletId;
      }
      if (labelsMarkUnvalidated.includes(label)) {
        draft[index].isValidated = false;
        draft[index].printKey = null;
        draft[index].rpadPropertyId = null;
      }
    });
    setProperties(newProperties);
  };
  const addSellersToNames = (names: any[]) => {
    const newOrderEntryNames = produce(orderEntryNames, draft => {
      for (const name of names) {
        if (name.trim()) {
          draft.push({ name: name.trim(), type: defaultInitialNameType });
        }
      }
    });
    setOrderEntryNames(
      newOrderEntryNames
        .filter(oe => oe.name)
        .reduce((pre, cur) => {
          if (pre.some(p => p.name === cur.name)) {
            return pre;
          }
          pre.push(cur);
          return pre;
        }, [] as OrderEntryName[])
    );
  };
  const deleteListOfNames = (names: string[]) => {
    setOrderEntryNames(orderEntryNames.filter(oen => !names.includes(oen.name)));
  };
  return (
    <OrderEntryContext.Provider
      value={{
        selectedClient,
        setSelectedClient,
        titleNumber,
        setTitleNumber,
        priority,
        setPriority,
        searchesAssignObj,
        setSearchesAssignObj,
        properties,
        setProperties,
        orderEntryNames,
        setOrderEntryNames,
        submitOrder,
        files,
        setFiles,
        isPurchase,
        setIsPurchase,
        submitting,
        errors,
        propertyErrors,
        validateProperty,
        templateSearchTypes,
        addedSearches,
        setTemplateSearchTypes,
        setAddedSearches,
        clientNote,
        setClientNote,
        openErrorModal,
        setOpenErrorModal,
        changePropertyInfo,
        addSellersToNames,
        deleteListOfNames,
        openSellerErrorModal,
        setOpenSellerErrorModal,
        fromAddressLookup,
      }}
    >
      {props.children}
    </OrderEntryContext.Provider>
  );
};

export function useOrderEntry() {
  const context = useContext(OrderEntryContext);
  return context;
}
